import { useCallback, useEffect, useRef, useState } from "react";

export interface UseSessionDatePickerOptions {
  /** Committed/selected dates from parent */
  selectedDates: Date[];
  /** Called when user applies selection */
  onApply: (dates: Date[]) => void;
  /**
   * When this value changes, temp selection is reset to [].
   * Use e.g. playerId to clear when switching players.
   */
  resetWhen?: unknown;
}

export interface UseSessionDatePickerResult {
  tempSelectedDates: Date[];
  setTempSelectedDates: React.Dispatch<React.SetStateAction<Date[]>>;
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
  handleCancel: () => void;
  handleApply: () => void;
  handleClear: (e: React.MouseEvent) => void;
  isCancellingRef: React.MutableRefObject<boolean>;
  prevPopoverOpenRef: React.MutableRefObject<boolean>;
}

/**
 * Manages temporary date selection and popover cancel/apply flow
 * for session date pickers. Used by DefinePlayerDataSet and SessionSelectStep.
 */
export function useSessionDatePicker({
  selectedDates,
  onApply,
  resetWhen,
}: UseSessionDatePickerOptions): UseSessionDatePickerResult {
  const [tempSelectedDates, setTempSelectedDates] =
    useState<Date[]>(selectedDates);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isCancellingRef = useRef(false);
  const prevPopoverOpenRef = useRef(false);

  const handleCancel = useCallback(() => {
    isCancellingRef.current = true;
    setTempSelectedDates(selectedDates);
  }, [selectedDates]);

  const handleApply = useCallback(() => {
    isCancellingRef.current = false;
    onApply(tempSelectedDates);
  }, [tempSelectedDates, onApply]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setTempSelectedDates([]);
      onApply([]);
    },
    [onApply],
  );

  // Reset temp when resetWhen changes (e.g. player switched). Only when provided.
  useEffect(() => {
    if (resetWhen !== undefined) {
      setTempSelectedDates([]);
    }
  }, [resetWhen]);

  // Sync temp when committed selectedDates change
  useEffect(() => {
    setTempSelectedDates(selectedDates);
  }, [selectedDates]);

  // Reset cancel flag when popover opens
  useEffect(() => {
    if (isPopoverOpen) {
      isCancellingRef.current = false;
    }
  }, [isPopoverOpen]);

  // Apply temp selection when popover closes (unless user cancelled)
  useEffect(() => {
    if (
      prevPopoverOpenRef.current &&
      !isPopoverOpen &&
      !isCancellingRef.current
    ) {
      onApply(tempSelectedDates);
    }
    prevPopoverOpenRef.current = isPopoverOpen;
    if (!isPopoverOpen) {
      isCancellingRef.current = false;
    }
  }, [isPopoverOpen, tempSelectedDates, onApply]);

  return {
    tempSelectedDates,
    setTempSelectedDates,
    isPopoverOpen,
    setIsPopoverOpen,
    handleCancel,
    handleApply,
    handleClear,
    isCancellingRef,
    prevPopoverOpenRef,
  };
}
